import React, { useMemo } from "react";
import GridRow from "./GridRow";
import { DEFAULT_ROW_HEIGHT } from "../../constants";

// Shared empty row for indices beyond the sheet's data (revealed by
// scrolling down). One frozen array keeps prop identity stable.
const EMPTY_ROW = Object.freeze([]);

/**
 * GridBody
 * The `<tbody>`.  Only the rows in the current scroll window (plus the
 * always-present frozen rows) are emitted as real <tr>s; the empty space
 * above and below is collapsed into two spacer rows whose heights
 * reproduce the full scroll height.  Hidden / filtered rows are skipped
 * (returning null) so the rendered TR list lines up with the freeze-pane
 * offset measurement.
 *
 * PERF: the workbook border post-processing (keep bottom/right, keep
 * top/left only when no visible neighbour carries the edge) is done HERE,
 * once per sheet, in a useMemo — not per cell per render. This gives
 * every cell a STABLE style object identity, which is what lets
 * React.memo(GridCell) skip ~thousands of cell re-renders during
 * selection drags and scrolling.
 *
 * PERF: virtualization (rowWindow) means a 50,000-row sheet mounts only
 * ~the visible slice of <tr>s instead of all 50,000 up front — this is
 * the dominant fix for slow initial data rendering.
 */
function GridBody({
  normalizedRows, maxCols, rowWindow,
  rowHeights, colWidths,
  cellStyles, cellRichHtml, cellMerges, cellLinks,
  numFmts,
  hiddenCols, hiddenRows,
  isRowVisible, isLastVisibleBeforeHidden, isLastVisibleBeforeHiddenRow,
  selectedCell, normalizedSelection, selectionType,
  frozenRows, frozenCols,
  measuredTheadHeight, frozenRowOffsets, frozenColOffsets,
  onSelectRow, onExtendRow, onHeaderContextMenu, onStartResize,
  onStartSelection, onExtendSelection,
  onLinkClick, onCellContextMenu,
  // Sort/Filter — the dropdowns now live on the first data row's cells.
  filters, activeFilterCol, isFilterColumn, getSortState,
  onToggleFilter, onApplyFilter, onCloseFilter,
  onSortAsc, onSortDesc, onClearSort, onCustomSort,
}) {
  // The row that hosts the Sort/Filter dropdowns — the first grid row.
  const FILTER_ROW = 0;
  // One context object, handed only to the filter row so every OTHER row's
  // cells keep stable props and stay memoized.  Memoized so the filter row
  // itself doesn't re-render on scroll — only when filter/sort state changes.
  const filterCtx = useMemo(() => (isFilterColumn ? {
    isFilterColumn, filters, activeFilterCol, normalizedRows, getSortState,
    onToggleFilter, onApplyFilter, onCloseFilter,
    onSortAsc, onSortDesc, onClearSort, onCustomSort,
  } : null), [
    isFilterColumn, filters, activeFilterCol, normalizedRows, getSortState,
    onToggleFilter, onApplyFilter, onCloseFilter,
    onSortAsc, onSortDesc, onClearSort, onCustomSort,
  ]);
  // Workbook borders: the parser propagates every authored edge to BOTH
  // adjacent cells (a top border on r,c is also the bottom border of
  // r-1,c). Each shared edge must be painted exactly ONCE — keep each
  // cell's bottom/right border and drop top/left, except where no
  // visible neighbour exists to carry that edge (first row/column, or
  // hidden neighbour).
  const processedStyles = useMemo(() => {
    const out = {};
    for (const key in cellStyles) {
      const raw = cellStyles[key];
      if (!raw) continue;
      const { borderTop, borderLeft, ...clean } = raw;
      const sep = key.indexOf(":");
      const r = parseInt(key.slice(0, sep), 10);
      const c = parseInt(key.slice(sep + 1), 10);
      if (borderTop && (r === 0 || !isRowVisible(r - 1))) clean.borderTop = borderTop;
      if (borderLeft && (c === 0 || hiddenCols.has(c - 1))) clean.borderLeft = borderLeft;
      out[key] = clean;
    }
    return out;
  }, [cellStyles, hiddenCols, isRowVisible]);

  // Render one data row (shared by the frozen-row band and the window).
  const renderRow = (rowIndex) => {
    if (!isRowVisible(rowIndex)) return null;
    // Rows beyond the sheet's parsed data render empty — this is what lets
    // the grid keep growing rows as the user scrolls down, like Excel.
    const row = normalizedRows[rowIndex] || EMPTY_ROW;
    const trClass = isLastVisibleBeforeHiddenRow(rowIndex) ? "sev-hidden-row-after" : "";
    const isFrozenRow = rowIndex < frozenRows;
    const isLastFrozenRow = rowIndex === frozenRows - 1;

    return (
      <GridRow
        key={`row-${rowIndex}`}
        rowIndex={rowIndex}
        row={row}
        rowHeight={rowHeights[rowIndex] || DEFAULT_ROW_HEIGHT}
        trClassName={trClass}
        cellStyles={processedStyles}
        numFmts={numFmts}
        cellRichHtml={cellRichHtml}
        cellMerges={cellMerges}
        cellLinks={cellLinks}
        hiddenCols={hiddenCols}
        selectedCell={selectedCell}
        normalizedSelection={normalizedSelection}
        selectionType={selectionType}
        isLastVisibleBeforeHidden={isLastVisibleBeforeHidden}
        colWidths={colWidths}
        maxCols={maxCols}
        isFrozenRow={isFrozenRow}
        isLastFrozenRow={isLastFrozenRow}
        frozenCols={frozenCols}
        measuredTheadHeight={measuredTheadHeight}
        frozenRowOffset={frozenRowOffsets[rowIndex]}
        frozenColOffsets={frozenColOffsets}
        onSelectRow={onSelectRow}
        onExtendRow={onExtendRow}
        onHeaderContextMenu={onHeaderContextMenu}
        onStartResize={onStartResize}
        onStartSelection={onStartSelection}
        onExtendSelection={onExtendSelection}
        onLinkClick={onLinkClick}
        onCellContextMenu={onCellContextMenu}
        filterCtx={rowIndex === FILTER_ROW ? filterCtx : null}
      />
    );
  };

  const { startRow, endRow, topPad, bottomPad } = rowWindow;
  // colSpan large enough to cover the row-header TH + every column.
  const spacerSpan = maxCols + 1;

  // Always-mounted frozen-row band ([0, frozenRows)); the window proper
  // begins at or after frozenRows so these never double-render.
  const frozenBand = [];
  for (let r = 0; r < frozenRows && r < normalizedRows.length; r += 1) {
    const el = renderRow(r);
    if (el) frozenBand.push(el);
  }

  const windowRows = [];
  for (let r = startRow; r < endRow; r += 1) {
    const el = renderRow(r);
    if (el) windowRows.push(el);
  }

  return (
    <tbody>
      {frozenBand}
      {topPad > 0 && (
        <tr aria-hidden="true" className="sev-virt-spacer" style={{ height: `${topPad}px` }}>
          <td colSpan={spacerSpan} style={{ height: `${topPad}px`, padding: 0, border: 0 }} />
        </tr>
      )}
      {windowRows}
      {bottomPad > 0 && (
        <tr aria-hidden="true" className="sev-virt-spacer" style={{ height: `${bottomPad}px` }}>
          <td colSpan={spacerSpan} style={{ height: `${bottomPad}px`, padding: 0, border: 0 }} />
        </tr>
      )}
    </tbody>
  );
}

export default React.memo(GridBody);
